import type { TimelineHour } from "../api/types";

import { GUST_ABS_THRESHOLD, GUST_DELTA, GUST_MIN } from "./windDisplay";

export function gustNotable(windMph: number | null, gustMph: number | null): boolean {
  if (gustMph === null) {
    return false;
  }
  if (gustMph >= GUST_ABS_THRESHOLD) {
    return true;
  }
  const wind = windMph ?? 0;
  return gustMph >= wind + GUST_DELTA && gustMph >= GUST_MIN;
}

export const SPARKLINE_PLOT_HEIGHT = 12;
export const SPARKLINE_PLOT_WIDTH = 18;
export const SPARKLINE_SCALE_FLOOR = 20;
export const SPARKLINE_SCALE_PADDING = 4;

export interface DayWindPeaks {
  maxWind: number;
  maxGust: number | null;
}

export function hourHasNotableGust(hour: TimelineHour): boolean {
  return gustNotable(hour.windMph, hour.gustMph);
}

export function hasGustSparkline(hours: TimelineHour[]): boolean {
  return hours.some(hourHasNotableGust);
}

export function scaleMaxForDay(hours: TimelineHour[]): number {
  const peak = Math.max(
    0,
    ...hours.map((hour) => {
      const wind = hour.windMph ?? 0;
      const gust = hourHasNotableGust(hour) && hour.gustMph !== null ? hour.gustMph : wind;
      return Math.max(wind, gust);
    }),
  );
  return Math.max(SPARKLINE_SCALE_FLOOR, peak + SPARKLINE_SCALE_PADDING);
}

export function mphToPlotY(mph: number, scaleMax: number): number {
  const clamped = Math.max(0, Math.min(mph, scaleMax));
  if (clamped <= 0) {
    return SPARKLINE_PLOT_HEIGHT - 1;
  }
  return Math.round(SPARKLINE_PLOT_HEIGHT - 1 - (clamped / scaleMax) * (SPARKLINE_PLOT_HEIGHT - 2));
}

export function sustainedMph(hour: TimelineHour): number {
  return hour.windMph ?? 0;
}

export function sparklinePoints(
  hours: TimelineHour[],
  mph: (hour: TimelineHour) => number,
  scaleMax: number,
): string {
  return hours
    .map((hour, index) => {
      const x = index + 0.5;
      const y = mphToPlotY(mph(hour), scaleMax);
      return `${x},${y}`;
    })
    .join(" ");
}

export function gustSparklineSegments(hours: TimelineHour[], scaleMax: number): string[] {
  const segments: string[] = [];
  let current: string[] = [];

  for (const [index, hour] of hours.entries()) {
    if (hourHasNotableGust(hour) && hour.gustMph !== null) {
      const x = index + 0.5;
      const y = mphToPlotY(hour.gustMph, scaleMax);
      current.push(`${x},${y}`);
    } else if (current.length > 0) {
      segments.push(current.join(" "));
      current = [];
    }
  }

  if (current.length > 0) {
    segments.push(current.join(" "));
  }

  return segments;
}

export function dayWindPeaks(hours: TimelineHour[]): DayWindPeaks {
  let maxWind = 0;
  let maxGust: number | null = null;

  for (const hour of hours) {
    const wind = sustainedMph(hour);
    if (wind > maxWind) {
      maxWind = wind;
    }

    if (hourHasNotableGust(hour) && hour.gustMph !== null) {
      if (maxGust === null || hour.gustMph > maxGust) {
        maxGust = hour.gustMph;
      }
    }
  }

  return { maxWind, maxGust };
}

export function formatWindSummary(peaks: DayWindPeaks): string {
  const wind = `${Math.round(peaks.maxWind)} mph`;
  if (peaks.maxGust === null) {
    return wind;
  }
  return `${wind} · gust ${Math.round(peaks.maxGust)}`;
}

export function shouldShowWindSparkline(hours: TimelineHour[]): boolean {
  return hours.some((hour) => hour.windMph !== null || hourHasNotableGust(hour));
}
